import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const BLOCK_PIXELS = 32;
const MIN_APPROVED_ZOOM = 2;
const MAX_APPROVED_ZOOM = 30;
const TRANSPARENT_ALPHA_THRESHOLD = 128;
const TRANSPARENT_PALETTE_COLOR = {
  hex: 'transparent',
  cleanHex: 'transparent',
  id: '1',
  name: 'Transparent',
  complement: 'ffffff',
  baseItem: 'ender_eye',
  r: 0,
  g: 0,
  b: 0,
  transparent: true,
};
const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];
const BAYER_2X2 = [
  [0, 2],
  [3, 1],
];
const DITHER_KERNELS = {
  'floyd-steinberg': {
    divisor: 16,
    weights: [
      { x: 1, y: 0, weight: 7 },
      { x: -1, y: 1, weight: 3 },
      { x: 0, y: 1, weight: 5 },
      { x: 1, y: 1, weight: 1 },
    ],
  },
  minavgerr: {
    divisor: 48,
    weights: [
      { x: 1, y: 0, weight: 7 },
      { x: 2, y: 0, weight: 5 },
      { x: -2, y: 1, weight: 3 },
      { x: -1, y: 1, weight: 5 },
      { x: 0, y: 1, weight: 7 },
      { x: 1, y: 1, weight: 5 },
      { x: 2, y: 1, weight: 3 },
      { x: -2, y: 2, weight: 1 },
      { x: -1, y: 2, weight: 3 },
      { x: 0, y: 2, weight: 5 },
      { x: 1, y: 2, weight: 3 },
      { x: 2, y: 2, weight: 1 },
    ],
  },
  burkes: {
    divisor: 32,
    weights: [
      { x: 1, y: 0, weight: 8 },
      { x: 2, y: 0, weight: 4 },
      { x: -2, y: 1, weight: 2 },
      { x: -1, y: 1, weight: 4 },
      { x: 0, y: 1, weight: 8 },
      { x: 1, y: 1, weight: 4 },
      { x: 2, y: 1, weight: 2 },
    ],
  },
  'sierra-lite': {
    divisor: 4,
    weights: [
      { x: 1, y: 0, weight: 2 },
      { x: -1, y: 1, weight: 1 },
      { x: 0, y: 1, weight: 1 },
    ],
  },
  stucki: {
    divisor: 42,
    weights: [
      { x: 1, y: 0, weight: 8 },
      { x: 2, y: 0, weight: 4 },
      { x: -2, y: 1, weight: 2 },
      { x: -1, y: 1, weight: 4 },
      { x: 0, y: 1, weight: 8 },
      { x: 1, y: 1, weight: 4 },
      { x: 2, y: 1, weight: 2 },
      { x: -2, y: 2, weight: 1 },
      { x: -1, y: 2, weight: 2 },
      { x: 0, y: 2, weight: 4 },
      { x: 1, y: 2, weight: 2 },
      { x: 2, y: 2, weight: 1 },
    ],
  },
  atkinson: {
    divisor: 8,
    weights: [
      { x: 1, y: 0, weight: 1 },
      { x: 2, y: 0, weight: 1 },
      { x: -1, y: 1, weight: 1 },
      { x: 0, y: 1, weight: 1 },
      { x: 1, y: 1, weight: 1 },
      { x: 0, y: 2, weight: 1 },
    ],
  },
};

function toWholeNumber(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function getCropBox(imageSize, ratioWidth, ratioHeight, cropScale, cropX, cropY) {
  if (!imageSize) {
    return null;
  }

  const imageRatio = imageSize.width / imageSize.height;
  const targetRatio = ratioWidth / ratioHeight;
  let maxWidth;
  let maxHeight;

  if (imageRatio > targetRatio) {
    maxHeight = imageSize.height;
    maxWidth = maxHeight * targetRatio;
  } else {
    maxWidth = imageSize.width;
    maxHeight = maxWidth / targetRatio;
  }

  const scale = cropScale / 100;
  const width = Math.max(1, Math.round(maxWidth * scale));
  const height = Math.max(1, Math.round(maxHeight * scale));
  const maxX = Math.max(0, imageSize.width - width);
  const maxY = Math.max(0, imageSize.height - height);

  return {
    x: Math.round((maxX * cropX) / 100),
    y: Math.round((maxY * cropY) / 100),
    width,
    height,
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clampChannel(value) {
  return clamp(Math.round(value), 0, 255);
}

export function parsePalette(text) {
  return text
    .split(/\r?\n/)
    .map((line) => {
      const match = line.trim().match(
        /^(#[0-9a-f]{6}),\s*(-?\d+)\s*\(([^)]+)\)\s*-\s*Complement\s+(#[0-9a-f]{6})\s*-\s*(\S+)\s*$/i
      );

      if (!match) {
        return null;
      }

      const [, hex, id, name, complement, baseItem] = match;
      const trimmedHex = hex.toLowerCase();

      return {
        hex: trimmedHex,
        cleanHex: trimmedHex.slice(1).toLowerCase(),
        id,
        name: name.trim(),
        complement: complement.slice(1).toLowerCase(),
        baseItem,
        r: Number.parseInt(trimmedHex.slice(1, 3), 16),
        g: Number.parseInt(trimmedHex.slice(3, 5), 16),
        b: Number.parseInt(trimmedHex.slice(5, 7), 16),
      };
    })
    .filter(Boolean);
}

function findNearestPaletteColor(red, green, blue, paletteColors) {
  let nearest = paletteColors[0];
  let nearestDistance = Infinity;

  paletteColors.forEach((color) => {
    const redDifference = red - color.r;
    const greenDifference = green - color.g;
    const blueDifference = blue - color.b;
    const distance =
      redDifference * redDifference + greenDifference * greenDifference + blueDifference * blueDifference;

    if (distance < nearestDistance) {
      nearest = color;
      nearestDistance = distance;
    }
  });

  return nearest;
}

function isTransparentAlpha(alpha) {
  return alpha < TRANSPARENT_ALPHA_THRESHOLD;
}

function adjustColor(red, green, blue, brightness, contrast, saturation) {
  const contrastFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));
  let adjustedRed = contrastFactor * (red - 128) + 128 + brightness;
  let adjustedGreen = contrastFactor * (green - 128) + 128 + brightness;
  let adjustedBlue = contrastFactor * (blue - 128) + 128 + brightness;
  const saturationFactor = 1 + saturation / 100;
  const luminance = 0.2126 * adjustedRed + 0.7152 * adjustedGreen + 0.0722 * adjustedBlue;

  adjustedRed = luminance + (adjustedRed - luminance) * saturationFactor;
  adjustedGreen = luminance + (adjustedGreen - luminance) * saturationFactor;
  adjustedBlue = luminance + (adjustedBlue - luminance) * saturationFactor;

  return [clampChannel(adjustedRed), clampChannel(adjustedGreen), clampChannel(adjustedBlue)];
}

function applyPaletteToCanvas(canvas, paletteColors, options) {
  const context = canvas.getContext('2d');
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  const ditherAmount = options.ditherStrength / 100;
  const diffusionKernel = DITHER_KERNELS[options.ditherMode];

  if (diffusionKernel && ditherAmount > 0) {
    const workingPixels = new Float32Array(canvas.width * canvas.height * 3);

    for (let index = 0, pixelIndex = 0; index < pixels.length; index += 4, pixelIndex += 3) {
      if (isTransparentAlpha(pixels[index + 3])) {
        workingPixels[pixelIndex] = Number.NaN;
        workingPixels[pixelIndex + 1] = Number.NaN;
        workingPixels[pixelIndex + 2] = Number.NaN;
        continue;
      }

      const [red, green, blue] = adjustColor(
        pixels[index],
        pixels[index + 1],
        pixels[index + 2],
        options.brightness,
        options.contrast,
        options.saturation
      );

      workingPixels[pixelIndex] = red;
      workingPixels[pixelIndex + 1] = green;
      workingPixels[pixelIndex + 2] = blue;
    }

    const addError = (x, y, redError, greenError, blueError, weight) => {
      if (x < 0 || x >= canvas.width || y < 0 || y >= canvas.height) {
        return;
      }

      const multiplier = weight / diffusionKernel.divisor;
      const index = (y * canvas.width + x) * 3;
      workingPixels[index] += redError * multiplier * ditherAmount;
      workingPixels[index + 1] += greenError * multiplier * ditherAmount;
      workingPixels[index + 2] += blueError * multiplier * ditherAmount;
    };

    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        const workingIndex = (y * canvas.width + x) * 3;
        const pixelIndex = (y * canvas.width + x) * 4;
        if (Number.isNaN(workingPixels[workingIndex])) {
          pixels[pixelIndex] = 0;
          pixels[pixelIndex + 1] = 0;
          pixels[pixelIndex + 2] = 0;
          pixels[pixelIndex + 3] = 0;
          continue;
        }

        const red = clampChannel(workingPixels[workingIndex]);
        const green = clampChannel(workingPixels[workingIndex + 1]);
        const blue = clampChannel(workingPixels[workingIndex + 2]);
        const nearest = findNearestPaletteColor(red, green, blue, paletteColors);
        const redError = red - nearest.r;
        const greenError = green - nearest.g;
        const blueError = blue - nearest.b;

        pixels[pixelIndex] = nearest.r;
        pixels[pixelIndex + 1] = nearest.g;
        pixels[pixelIndex + 2] = nearest.b;
        pixels[pixelIndex + 3] = 255;

        diffusionKernel.weights.forEach((entry) => {
          addError(x + entry.x, y + entry.y, redError, greenError, blueError, entry.weight);
        });
      }
    }

    context.putImageData(imageData, 0, 0);
    return;
  }

  for (let index = 0; index < pixels.length; index += 4) {
    if (isTransparentAlpha(pixels[index + 3])) {
      pixels[index] = 0;
      pixels[index + 1] = 0;
      pixels[index + 2] = 0;
      pixels[index + 3] = 0;
      continue;
    }

    const pixelNumber = index / 4;
    const x = pixelNumber % canvas.width;
    const y = Math.floor(pixelNumber / canvas.width);
    const orderedMatrix =
      options.ditherMode === 'ordered' || options.ditherMode === 'bayer-4x4'
        ? BAYER_4X4
        : options.ditherMode === 'bayer-2x2'
          ? BAYER_2X2
          : null;
    const orderedOffset =
      orderedMatrix
        ? ((orderedMatrix[y % orderedMatrix.length][x % orderedMatrix.length] + 0.5) /
            (orderedMatrix.length * orderedMatrix.length) -
            0.5) *
          96 *
          ditherAmount
        : 0;
    const [red, green, blue] = adjustColor(
      pixels[index],
      pixels[index + 1],
      pixels[index + 2],
      options.brightness + orderedOffset,
      options.contrast,
      options.saturation
    );
    const nearest = findNearestPaletteColor(red, green, blue, paletteColors);

    pixels[index] = nearest.r;
    pixels[index + 1] = nearest.g;
    pixels[index + 2] = nearest.b;
    pixels[index + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);
}

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function getSafeFileName(fileName) {
  const baseName = fileName.replace(/\.[^/.]+$/, '') || 'blockmap';
  return `${baseName.replace(/[^a-z0-9-_]+/gi, '_')}.oxs`;
}

export function generateOxs(canvas, paletteColors) {
  const context = canvas.getContext('2d');
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  const paletteByHex = new Map(paletteColors.map((color) => [color.cleanHex, color]));
  const usedPalette = [];
  const usedPaletteIndexByHex = new Map();
  const stitches = [];

  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      const pixelIndex = (y * canvas.width + x) * 4;
      const transparentPixel = isTransparentAlpha(pixels[pixelIndex + 3]);
      const hex = transparentPixel
        ? TRANSPARENT_PALETTE_COLOR.cleanHex
        : [pixels[pixelIndex], pixels[pixelIndex + 1], pixels[pixelIndex + 2]]
            .map((value) => value.toString(16).padStart(2, '0'))
            .join('');
      let paletteColor = transparentPixel ? TRANSPARENT_PALETTE_COLOR : paletteByHex.get(hex);

      if (!paletteColor) {
        paletteColor = findNearestPaletteColor(pixels[pixelIndex], pixels[pixelIndex + 1], pixels[pixelIndex + 2], paletteColors);
      }

      const paletteKey = paletteColor.transparent ? TRANSPARENT_PALETTE_COLOR.cleanHex : paletteColor.cleanHex;
      if (!usedPaletteIndexByHex.has(paletteKey)) {
        usedPaletteIndexByHex.set(paletteKey, usedPalette.length + 1);
        usedPalette.push(paletteColor);
      }

      stitches.push({
        x,
        y,
        palindex: usedPaletteIndexByHex.get(paletteKey),
      });
    }
  }

  const lines = [
    '<chart>',
    '<format comments01="Created by Artmatica Generator"/>',
    `<properties oxsversion="1.0" software="Artmatica Generator" software_version="1.0" chartheight="${canvas.height}" chartwidth="${canvas.width}" charttitle="" author="" copyright="" instructions="" stitchesperinch="18" stitchesperinch_y="18" palettecount="${usedPalette.length}" misc1="" misc2=""/>`,
    '<palette>',
  ];

  usedPalette.forEach((color, index) => {
    const paletteIndex = index + 1;
    const number = escapeXml(color.id);
    const name = escapeXml(color.name);
    const baseItem = escapeXml(color.baseItem);
    const cleanHex = color.transparent ? '000000' : color.cleanHex;

    lines.push(
      `<palette_item index="${paletteIndex}" number="${number}" name="${name}" color="${cleanHex}" printcolor="${color.complement}" blendcolor="nil" comments="" strands="2" symbol="${paletteIndex}" dashpattern="" misc1="${baseItem}" bsstrands="2" bscolor="${cleanHex}"/>`
    );
  });

  lines.push('</palette>', '<fullstitches>');

  stitches.forEach((stitch) => {
    lines.push(`<stitch x="${stitch.x}" y="${stitch.y}" palindex="${stitch.palindex}"/>`);
  });

  lines.push(
    '</fullstitches>',
    '<partstitches><partstitch/></partstitches><backstitches><backstitch/></backstitches><ornaments_inc_knots_and_beads><object/></ornaments_inc_knots_and_beads><commentboxes/>',
    '</chart>'
  );

  return `${lines.join('\n')}\n`;
}

function App() {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const cropStageRef = useRef(null);
  const dragRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [aspectWidth, setAspectWidth] = useState('1');
  const [aspectHeight, setAspectHeight] = useState('1');
  const [cropScale, setCropScale] = useState(100);
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);
  const [imageSize, setImageSize] = useState(null);
  const [displaySize, setDisplaySize] = useState(null);
  const [approvedCrop, setApprovedCrop] = useState(null);
  const [approvedZoom, setApprovedZoom] = useState(10);
  const [paletteColors, setPaletteColors] = useState([]);
  const [paletteError, setPaletteError] = useState('');
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [ditherMode, setDitherMode] = useState('none');
  const [ditherStrength, setDitherStrength] = useState(50);
  const cropIsApproved = approvedCrop !== null;

  const ratioWidth = toWholeNumber(aspectWidth, 1);
  const ratioHeight = toWholeNumber(aspectHeight, 1);
  const cropBox = useMemo(
    () => getCropBox(imageSize, ratioWidth, ratioHeight, cropScale, cropX, cropY),
    [imageSize, ratioWidth, ratioHeight, cropScale, cropX, cropY]
  );

  useEffect(() => {
    const cropStage = cropStageRef.current;

    if (!approvedCrop || !cropStage) {
      return;
    }

    const handleWheelZoom = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY > 0 ? -1 : 1;
      setApprovedZoom((currentZoom) => clamp(currentZoom + direction, MIN_APPROVED_ZOOM, MAX_APPROVED_ZOOM));
    };

    cropStage.addEventListener('wheel', handleWheelZoom, { passive: false });

    return () => {
      cropStage.removeEventListener('wheel', handleWheelZoom);
    };
  }, [approvedCrop]);

  useEffect(() => {
    if (typeof fetch === 'undefined') {
      return;
    }

    fetch('/palette.txt')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Palette file could not be loaded.');
        }

        return response.text();
      })
      .then((text) => {
        const colors = parsePalette(text);

        if (colors.length === 0) {
          throw new Error('Palette file does not contain valid hex colors.');
        }

        setPaletteColors(colors);
        setPaletteError('');
      })
      .catch((fetchError) => {
        setPaletteColors([]);
        setPaletteError(fetchError.message);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  useEffect(() => {
    if (!cropIsApproved) {
      return;
    }

    renderApprovedCrop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brightness, contrast, saturation, ditherMode, ditherStrength, cropIsApproved]);

  useEffect(() => {
    if (!preview || !imageRef.current) {
      setDisplaySize(null);
      return;
    }

    const updateDisplaySize = () => {
      if (!imageRef.current) {
        return;
      }

      const bounds = imageRef.current.getBoundingClientRect();
      setDisplaySize({
        width: bounds.width,
        height: bounds.height,
      });
    };

    updateDisplaySize();

    const observer = new ResizeObserver(updateDisplaySize);
    observer.observe(imageRef.current);
    window.addEventListener('resize', updateDisplaySize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateDisplaySize);
    };
  }, [preview]);

  const overlayBox =
    cropBox && imageSize && displaySize
      ? {
          left: (cropBox.x / imageSize.width) * displaySize.width,
          top: (cropBox.y / imageSize.height) * displaySize.height,
          width: (cropBox.width / imageSize.width) * displaySize.width,
          height: (cropBox.height / imageSize.height) * displaySize.height,
        }
      : null;

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Choose a PNG, JPG, GIF, WebP, or other image file.');
      setFileName('');
      setPreview(null);
      setImageSize(null);
      setApprovedCrop(null);
      setApprovedZoom(10);
      event.target.value = '';
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setError('');
    setFileName(file.name);
    setCropScale(100);
    setCropX(50);
    setCropY(50);
    setImageSize(null);
    setApprovedCrop(null);
    setApprovedZoom(10);
    setPreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }
      return imageUrl;
    });
  };

  const handleImageLoad = (event) => {
    const image = event.currentTarget;
    setImageSize({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    setDisplaySize({
      width: image.getBoundingClientRect().width,
      height: image.getBoundingClientRect().height,
    });
  };

  const handleAspectChange = (setter) => (event) => {
    const nextValue = event.target.value;

    if (nextValue === '' || /^\d+$/.test(nextValue)) {
      setter(nextValue);
      setApprovedCrop(null);
    }
  };

  const normalizeAspectValue = (setter) => (event) => {
    setter(String(toWholeNumber(event.target.value, 1)));
  };

  const handleCropDragStart = (event) => {
    if (!cropBox || !imageSize || !displaySize) {
      return;
    }

    const pointer = event.touches?.[0] ?? event;
    dragRef.current = {
      pointerX: pointer.clientX,
      pointerY: pointer.clientY,
      cropX,
      cropY,
      maxX: Math.max(0, imageSize.width - cropBox.width),
      maxY: Math.max(0, imageSize.height - cropBox.height),
    };

    window.addEventListener('mousemove', handleCropDragMove);
    window.addEventListener('mouseup', handleCropDragEnd);
    window.addEventListener('touchmove', handleCropDragMove);
    window.addEventListener('touchend', handleCropDragEnd);
  };

  const handleCropDragMove = (event) => {
    if (!dragRef.current || !displaySize || !imageSize) {
      return;
    }

    const pointer = event.touches?.[0] ?? event;
    const deltaX = ((pointer.clientX - dragRef.current.pointerX) / displaySize.width) * imageSize.width;
    const deltaY = ((pointer.clientY - dragRef.current.pointerY) / displaySize.height) * imageSize.height;
    const nextX =
      dragRef.current.maxX === 0
        ? 50
        : ((dragRef.current.cropX / 100) * dragRef.current.maxX + deltaX) / dragRef.current.maxX * 100;
    const nextY =
      dragRef.current.maxY === 0
        ? 50
        : ((dragRef.current.cropY / 100) * dragRef.current.maxY + deltaY) / dragRef.current.maxY * 100;

    setCropX(Math.min(100, Math.max(0, nextX)));
    setCropY(Math.min(100, Math.max(0, nextY)));
    setApprovedCrop(null);
  };

  const updatePreprocessingValue = (setter) => (event) => {
    setter(Number(event.target.value));
  };

  const updateDitherMode = (event) => {
    setDitherMode(event.target.value);
  };

  const handleCropDragEnd = () => {
    dragRef.current = null;
    window.removeEventListener('mousemove', handleCropDragMove);
    window.removeEventListener('mouseup', handleCropDragEnd);
    window.removeEventListener('touchmove', handleCropDragMove);
    window.removeEventListener('touchend', handleCropDragEnd);
  };

  const renderApprovedCrop = () => {
    if (!cropBox || !canvasRef.current || !preview || paletteColors.length === 0) {
      return;
    }

    const sourceImage = new Image();
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const outputWidth = ratioWidth * BLOCK_PIXELS;
    const outputHeight = ratioHeight * BLOCK_PIXELS;

    sourceImage.onload = () => {
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.clearRect(0, 0, outputWidth, outputHeight);
      context.drawImage(
        sourceImage,
        cropBox.x,
        cropBox.y,
        cropBox.width,
        cropBox.height,
        0,
        0,
        outputWidth,
        outputHeight
      );
      applyPaletteToCanvas(canvas, paletteColors, {
        brightness,
        contrast,
        saturation,
        ditherMode,
        ditherStrength,
      });

      setApprovedCrop({
        ratio: `${ratioWidth}:${ratioHeight}`,
        sourceWidth: cropBox.width,
        sourceHeight: cropBox.height,
        outputWidth,
        outputHeight,
        imageUrl: canvas.toDataURL('image/png'),
        paletteColorCount: paletteColors.length,
        ditherMode,
      });
    };

    sourceImage.src = preview;
  };

  const approveCrop = () => {
    renderApprovedCrop();
  };

  const downloadOxs = () => {
    if (!approvedCrop || !canvasRef.current || paletteColors.length === 0) {
      return;
    }

    const oxsContent = generateOxs(canvasRef.current, paletteColors);
    const blob = new Blob([oxsContent], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = getSafeFileName(fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="app-shell">
      <main className="workspace" aria-labelledby="app-title">
        <section className="intro-panel">
          <div className="intro-copy">
            <p className="eyebrow">Minecraft pixel art planner</p>
            <h1 id="app-title">Artmatica Generator</h1>
            <p className="summary">
              Upload an image, crop, adjust, and export as an .oxs file. Drop it in your /config/artmatica folder for use with Artmatica.
            </p>

            <label className="upload-zone">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                aria-describedby={error ? 'upload-error' : undefined}
              />
              <span className="upload-title">Upload image</span>
              <span className="upload-help">PNG, JPG, GIF, WebP, and other browser-supported image files</span>
            </label>

            {error && (
              <p className="error-message" id="upload-error" role="alert">
                {error}
              </p>
            )}

            {paletteError && (
              <p className="error-message" role="alert">
                {paletteError}
              </p>
            )}
          </div>

          <div className="controls-panel" aria-label="Crop controls">
            <div className="control-heading">
              <span>Aspect ratio</span>
              <strong>
                {ratioWidth}:{ratioHeight}
              </strong>
            </div>

            <div className="ratio-row">
              <label>
                <span>X</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={aspectWidth}
                  onChange={handleAspectChange(setAspectWidth)}
                  onBlur={normalizeAspectValue(setAspectWidth)}
                  aria-label="Aspect ratio X"
                />
              </label>
              <span className="ratio-separator">:</span>
              <label>
                <span>Y</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={aspectHeight}
                  onChange={handleAspectChange(setAspectHeight)}
                  onBlur={normalizeAspectValue(setAspectHeight)}
                  aria-label="Aspect ratio Y"
                />
              </label>
            </div>

            <label className="range-control">
              <span>Crop size</span>
              <input
                type="range"
                min="10"
                max="100"
                value={cropScale}
                disabled={!preview}
                onChange={(event) => {
                  setCropScale(Number(event.target.value));
                  setApprovedCrop(null);
                }}
              />
            </label>

            <div className="control-heading">
              <span>Preprocess</span>
            </div>

            <label className="range-control value-control">
              <span>Brightness</span>
              <strong>{brightness}</strong>
              <input
                type="range"
                min="-100"
                max="100"
                value={brightness}
                disabled={!preview}
                onChange={updatePreprocessingValue(setBrightness)}
              />
            </label>

            <label className="range-control value-control">
              <span>Contrast</span>
              <strong>{contrast}</strong>
              <input
                type="range"
                min="-100"
                max="100"
                value={contrast}
                disabled={!preview}
                onChange={updatePreprocessingValue(setContrast)}
              />
            </label>

            <label className="range-control value-control">
              <span>Saturation</span>
              <strong>{saturation}</strong>
              <input
                type="range"
                min="-100"
                max="100"
                value={saturation}
                disabled={!preview}
                onChange={updatePreprocessingValue(setSaturation)}
              />
            </label>

            <label className="select-control">
              <span>Dithering</span>
              <select value={ditherMode} disabled={!preview} onChange={updateDitherMode}>
                <option value="none">None</option>
                <option value="bayer-2x2">Bayer 2x2</option>
                <option value="bayer-4x4">Bayer 4x4</option>
                <option value="floyd-steinberg">Floyd-Steinberg</option>
                <option value="minavgerr">MinAvgErr</option>
                <option value="burkes">Burkes</option>
                <option value="sierra-lite">Sierra Lite</option>
                <option value="stucki">Stucki</option>
                <option value="atkinson">Atkinson</option>
              </select>
            </label>

            <label className="range-control value-control">
              <span>Dither strength</span>
              <strong>{ditherStrength}%</strong>
              <input
                type="range"
                min="0"
                max="100"
                value={ditherStrength}
                disabled={!preview || ditherMode === 'none'}
                onChange={updatePreprocessingValue(setDitherStrength)}
              />
            </label>

            <button
              className="approve-button"
              type="button"
              disabled={!preview || !cropBox || paletteColors.length === 0}
              onClick={approveCrop}
            >
              Approve crop
            </button>

            {approvedCrop && (
              <p className="approved-message" role="status">
                Approved {approvedCrop.ratio} crop at {approvedCrop.outputWidth} x {approvedCrop.outputHeight}px using{' '}
                {approvedCrop.paletteColorCount} palette colors.
              </p>
            )}

            <button
              className="download-button"
              type="button"
              disabled={!approvedCrop}
              onClick={downloadOxs}
            >
              Download .oxs
            </button>

          </div>
        </section>

        <section className="preview-panel" aria-label="Image crop preview">
          {preview ? (
            <>
              <div
                ref={cropStageRef}
                className={`crop-stage${approvedCrop ? ' approved-stage' : ''}`}
              >
                {approvedCrop ? (
                  <>
                    <div className="approved-image-wrap">
                      <img
                        className="approved-image"
                        src={approvedCrop.imageUrl}
                        alt={`Approved crop of ${fileName}`}
                        style={{
                          width: approvedCrop.outputWidth * approvedZoom,
                          height: approvedCrop.outputHeight * approvedZoom,
                        }}
                      />
                    </div>
                    <label className="zoom-hud">
                      <span>Zoom</span>
                      <input
                        type="range"
                        min={MIN_APPROVED_ZOOM}
                        max={MAX_APPROVED_ZOOM}
                        value={approvedZoom}
                        onChange={(event) => setApprovedZoom(Number(event.target.value))}
                      />
                      <strong>{approvedZoom}x</strong>
                    </label>
                  </>
                ) : (
                  <img ref={imageRef} src={preview} alt={`Preview of ${fileName}`} onLoad={handleImageLoad} />
                )}

                {!approvedCrop && overlayBox && (
                  <div className="crop-overlay" style={{ width: displaySize.width, height: displaySize.height }}>
                    <div className="shade shade-top" style={{ height: overlayBox.top }} />
                    <div
                      className="shade shade-right"
                      style={{
                        top: overlayBox.top,
                        left: overlayBox.left + overlayBox.width,
                        width: displaySize.width - overlayBox.left - overlayBox.width,
                        height: overlayBox.height,
                      }}
                    />
                    <div
                      className="shade shade-bottom"
                      style={{
                        top: overlayBox.top + overlayBox.height,
                        height: displaySize.height - overlayBox.top - overlayBox.height,
                      }}
                    />
                    <div
                      className="shade shade-left"
                      style={{
                        top: overlayBox.top,
                        width: overlayBox.left,
                        height: overlayBox.height,
                      }}
                    />
                    <button
                      className="crop-box"
                      type="button"
                      style={overlayBox}
                      onMouseDown={handleCropDragStart}
                      onTouchStart={handleCropDragStart}
                      aria-label="Drag crop selection"
                    >
                      <span />
                    </button>
                  </div>
                )}
              </div>

              <div className="file-details">
                <p className="file-name">{fileName}</p>
                {approvedCrop ? (
                  <p>
                    Output: {approvedCrop.outputWidth} x {approvedCrop.outputHeight}px
                  </p>
                ) : cropBox ? (
                  <p>
                    Crop: {cropBox.width} x {cropBox.height}px
                  </p>
                ) : null}
              </div>

              <canvas ref={canvasRef} className="crop-canvas" aria-hidden="true" />
            </>
          ) : (
            <div className="empty-preview">
              <div className="pixel-grid" aria-hidden="true" />
              <p>No image uploaded yet</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
